import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LinkedIcon from "@assessment/assets/images/linkedInIcon.png";
import gloIcon from "@assessment/assets/images/gloIcon.png";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { DocumentTextIcon, BookOpenIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { useGetSkillByIdMutation } from "@lxp-app/pages/create-session/slices/utilitySlice";
import { useSelector } from "react-redux";
import { RootState } from "@lxp-app/store/store";
import ShareCertificateModal, {
  getShareCertificateUrl
} from "@lxp-app/pages/my-activity-dashboard/pages/components/shareCertificateModal.tsx";
import LearningWallIcon from "@lxp-app/assets/svg/Learning_wall.svg";
import { useLazyGetSkillsQuery } from "@lxp-app/utils/getSkills";
import { usePublishNoteMutation } from "@lxp-app/pages/learning-canvas/learningCanvasSlice";
import { ShareModal } from "@lxp-app/pages/learning-canvas/components/modals/ShareModal";
import { ShowToast } from "@lxp-app/utils/showToast";

interface WhatsNextProps {
  passed: boolean;
  scheduleId: string;
  skills: string[];
  certificationDetails: any;
  accessDetails: any;
}

const WhatsNext: React.FC<WhatsNextProps> = ({
  passed,
  scheduleId,
  skills,
  certificationDetails,
  accessDetails
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [skillApiByIds, skillDetails] = useGetSkillByIdMutation();
  const searchString = useRef("");
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [publishNote] = usePublishNoteMutation();
  const [convertedSkills, setConvertedSkills] = useState<any>(null);
  const [templateData, setTemplateData] = useState<any>({
    title: "",
    content: "",
    tags: []
  });
  const shareConfig = useSelector(
    (state: RootState) =>
      state.AppMetaDataSlice.tenantConfig?.pages?.assessment?.assessmentResult
        ?.shareCertificate
  );
  const userRoles = useSelector(
    (state: RootState) => state.UserInfoSlice?.profile?.role
  );

  const isShareEnabledForTenant = shareConfig?.enabled === true;
  const isShareAllowedForRole = (() => {
    const allow = (shareConfig?.enabledForRole ?? "*").trim();
    if (allow === "*") return true;
    const allowed = allow
      .split(",")
      .map((r) => r.trim().toLowerCase())
      .filter(Boolean);
    const mine = ((userRoles as unknown as string[]) ?? []).map((r) =>
      String(r).toLowerCase()
    );
    return allowed.some((r) => mine.includes(r));
  })();
  const hasListedChannel =
    !!shareConfig?.channels &&
    Object.values(shareConfig.channels).some(Boolean);
  const canShowShare =
    isShareEnabledForTenant && isShareAllowedForRole && hasListedChannel;
  const canShare = !!getShareCertificateUrl(certificationDetails);

  const handleLinkedInShare = () => {
    setIsShareOpen(true);
  };

  const handleViewCertificate = () => {
    navigate(`/profile/activity-dashboard/certifications`);
  };

  const handleLearningWallShare = () => {
    // Implement Learning Wall sharing logic here
    setShareOpen(true);
    setTemplateData(
      getAssessmentShareTemplate(assessmentShareTemplates, variables)
    );
  };

  const handleLearningResources = () => {
    console.log(searchString.current);

    if (searchString.current.length > 0) {
      navigate(`/search?searchRequest=${searchString.current}`);
    }
  };

  const handleRetakeAssessment = () => {
    navigate(`/assessment/${scheduleId}`);
  };

  const getCertificateIcon = () => {
    return (
      <div className="p-4 bg-[#F5F3FF] flex justify-center items-center rounded-2xl size-16 text-[#7C3AED]">
        <DocumentTextIcon className="size-7" />
      </div>
    );
  };

  const getLearningResourcesIcon = () => {
    return (
      <div className="p-4 bg-[#FFFBEB] flex justify-center items-center rounded-2xl size-16 text-[#D97706]">
        <BookOpenIcon className="size-7" />
      </div>
    );
  };

  const getRetakeAssessmentIcon = () => {
    return (
      <div className="p-4 bg-[#EFF6FF] flex justify-center items-center rounded-2xl size-16 text-[#3B82F6]">
        <BookOpenIcon className="size-7" />
      </div>
    );
  };

  const passedCardData = [
    {
      name: "linkedIn",
      icon: LinkedIcon,
      image: true,
      title: t("assessment.whatsNext.passed.shareLinkedInTitle"),
      subtitle: t("assessment.whatsNext.passed.shareLinkedInSubtitle"),
      onclick: handleLinkedInShare,
      disabled: !canShowShare || !canShare
    },
    {
      name: "ViewCertificate",
      icon: getCertificateIcon(),
      image: false,
      title: t("assessment.whatsNext.passed.viewCertificateTitle"),
      subtitle: t("assessment.whatsNext.passed.viewCertificateSubtitle"),
      onclick: handleViewCertificate,
      disabled: false
    },
    {
      name: "learningWall",
      icon: LearningWallIcon,
      image: true,
      title: t("assessment.whatsNext.passed.shareGloTitle"),
      subtitle: t("assessment.whatsNext.passed.shareGloSubtitle"),
      onclick: handleLearningWallShare,
      disabled: false
    }
  ];

  const failedCardData = [
    {
      name: "reviewLearningResources",
      icon: getLearningResourcesIcon(),
      image: false,
      title: t("assessment.whatsNext.failed.reviewLearningResourcesTitle"),
      subtitle: t(
        "assessment.whatsNext.failed.reviewLearningResourcesSubtitle"
      ),
      onclick: handleLearningResources,
      disabled: false
    },
    {
      name: "retakeAssessment",
      icon: getRetakeAssessmentIcon(),
      image: false,
      title: t("assessment.whatsNext.failed.retakeAssessmentTitle"),
      subtitle: t("assessment.whatsNext.failed.retakeAssessmentSubtitle"),
      onclick: handleRetakeAssessment,
      disabled:
        accessDetails?.attempts?.completed >=
        accessDetails?.attempts?.maxAttempts,
      attemptDetails: accessDetails?.attempts || null
    }
  ];

  const getParentSkill = (parentSkill: any) => {
    let parentSkillName = "";
    if (parentSkill && skillDetails?.data?.data) {
      for (const skill of skillDetails.data.data) {
        if (parentSkill.id === skill.id) {
          parentSkillName = ", " + skill.preferredSkillName.en_UK;
          break;
        }
      }
    }
    return parentSkillName;
  };

  useEffect(() => {
    if (skills && skills.length > 0) {
      const payload = {
        ids: [...skills]
      };
      skillApiByIds(payload).then((skills) => {
        setConvertedSkills(skills.data.data);
        skills.data.data.forEach((skill: any) => {
          searchString.current +=
            "&profileSkills=" +
            (skill?.preferredSkillName?.en_UK
              ? skill?.preferredSkillName?.en_UK +
                getParentSkill(skill.parentskill)
              : skill?.preferredSkillName?.en_US +
                getParentSkill(skill.parentskill));
        });
      });
    }
  }, [skills]);

  const assessmentShareTemplates = {
    title: "Certified in {{certificationName}}!",
    content:
      "Excited to have successfully earned the {{certificationName}} certification! It was a rewarding learning journey, and I'm looking forward to applying these skills in my work. Check out my certificate: {{certificateUrl}}",
    tags: ["#Certification", "#Certified", "#{{primarySkill}}"]
  };

  const variables = {
    certificationName: accessDetails?.title,
    certificateUrl: certificationDetails?.certificateUrl || "",
    primarySkill:
      convertedSkills
        ?.map((skill: any) => skill.preferredSkillName.en_UK)
        .join(" #") || ""
  };

  const getAssessmentShareTemplate = (template: any, variables: any = {}) => {
    // Find the template matching the progress range (inclusive)

    if (!template) {
      return null;
    }

    // Helper function to replace {{variableName}} placeholders
    const interpolate = (text: string) => {
      return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
        return variables[key] !== undefined ? variables[key] : match;
      });
    };

    return {
      title: interpolate(template.title),
      content: interpolate(template.content),
      tags: template.tags.map(interpolate).join(" ")
    };
  };

  const publish = async (data: any) => {
    if (Object.keys(data).length > 0)
      await publishNote({ data: data })
        .unwrap()
        .then((res) => {
          if (res && res.data && res.data.id) {
            ShowToast({
              label: "Post uploaded successfully",
              severity: "success"
            });
            navigate("/learning-canvas", { state: { refetch: true } });
          }
          if (res && res.error && res.error.length > 0) {
            ShowToast({
              label:
                res.error?.message || "Error uploading post, please try again",
              severity: "error"
            });
          }
        })
        .catch((err) => {
          ShowToast({
            label: "Error uploading post, please try again",
            severity: "error"
          });
        });
  };

  return (
    <div className="mt-20 pb-4">
      <div className="flex items-center gap-x-3 w-full">
        <p className="uppercase w-fit font-bold text-base text-[#9CA3AF] dark:text-white">
          {t("assessment.whatsNext.heading")}
        </p>
        <hr className="border-[#F1F5F9] border-2 dark:border-white w-[calc(100%-122px)]" />
      </div>

      <div className="flex gap-6 flex-wrap mt-9">
        {(passed ? passedCardData : failedCardData).map((card: any, index) => (
          <div
            key={index}
            className={`p-9 w-[378px] rounded-3xl  border-[1.5px] ${card.disabled ? "cursor-not-allowed bg-[#F5F5F5] dark:bg-gray-500 opacity-50" : "cursor-pointer bg-white dark:bg-[#1C1C1E]"} shadow-sm relative`}
            onClick={() => {
              if (!card.disabled) {
                card.onclick();
              }
            }}
          >
            {!card.image ? (
              card.icon
            ) : (
              <img
                src={card.icon as string}
                alt={card.name}
                className={`${card.name == "glo" ? "h-14" : "h-16"}`}
              />
            )}
            <div className="flex items-center gap-x-2 mt-6 mb-2">
              <p className="text-xl font-semibold">{card.title}</p>
              <ChevronRightIcon className="size-5" />
            </div>
            <p className="text-lg text-[#6B7280] dark:text-white">
              {card.subtitle}
            </p>
            {card?.attemptDetails && (
              <div className="rounded-2xl px-2.5 py-1.5 font-semibold text-xs w-fit h-fit absolute top-9 right-9 text-[#B45309] bg-amber-50">
                {card.attemptDetails.maxAttempts -
                  card.attemptDetails.completed}{" "}
                {t("assessment.whatsNext.attemptsRemaining")}
              </div>
            )}
          </div>
        ))}
      </div>

      {canShowShare && (
        <ShareCertificateModal
          open={isShareOpen}
          setOpen={setIsShareOpen}
          certificate={certificationDetails}
          fromAssessmentResult={true}
          labels={{
            title: t("assessment.whatsNext.modal.title"),
            linkedInPostLabel: t(
              "assessment.whatsNext.modal.linkedInPostLabel"
            ),
            linkedInProfileLabel: t(
              "assessment.whatsNext.modal.linkedInProfileLabel"
            ),
            gloLabel: t("assessment.whatsNext.modal.gloLabel"),
            comingSoon: t("assessment.whatsNext.modal.comingSoon"),
            infoMessage: t("assessment.whatsNext.modal.infoMessage")
          }}
        />
      )}

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        onPublish={(data) => {
          publish({
            ...data,
            contentId: certificationDetails?.parentId
          });
        }}
        defaultContent={templateData.content}
        defaultTitle={templateData.title}
        defaultTags={templateData.tags}
        defaultType="assessment"
      />
    </div>
  );
};

export default WhatsNext;
